import { create } from "zustand";
import { getDataSource } from "../data";
import { endSession, sessionGuard } from "./session";
import type { Member, Workspace, WorkspaceSummary } from "../types/domain";
import type { InviteDraft, WorkspaceDraft } from "../data/types";

interface WorkspaceState {
  workspaces: Workspace[];
  currentId: string | null;
  members: Member[];
  allWorkspaces: WorkspaceSummary[];
  loading: boolean;
  error: string | null;

  load: () => Promise<void>;
  loadMembers: () => Promise<void>;
  loadAllWorkspaces: () => Promise<void>;
  select: (id: string) => void;
  create: (draft: WorkspaceDraft) => Promise<Workspace>;
  invite: (draft: InviteDraft) => Promise<Member>;
  reset: () => void;
}

const LAST_WS_KEY = "vf-console-workspace";

/**
 * Monotonic load id: only the newest listWorkspaces may write the list or clear
 * the loading flag. LiveRoot reloads on identity change and React re-invokes
 * effects, so two loads can overlap — the slower one must not win.
 */
let loadSeq = 0;

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  workspaces: [],
  currentId: null,
  members: [],
  allWorkspaces: [],
  loading: false,
  error: null,

  load: async () => {
    const alive = sessionGuard();
    const ticket = ++loadSeq;
    set({ loading: true, error: null });
    // A stale session drops the list but still releases the loading flag, so a
    // reset landing mid-flight cannot leave the shell spinning. Safe only
    // because we hold the newest ticket.
    const settle = () => set({ loading: false });
    try {
      const workspaces = await getDataSource().listWorkspaces();
      if (ticket !== loadSeq) return;
      // User A's list must never choose user B's current workspace.
      if (!alive()) return settle();
      // Prefer the workspace the user was last in, when it still exists.
      const remembered = localStorage.getItem(LAST_WS_KEY);
      const current =
        workspaces.find((w) => w.id === get().currentId) ??
        workspaces.find((w) => w.id === remembered) ??
        workspaces[0];
      set({ workspaces, currentId: current?.id ?? null, loading: false });
    } catch (e) {
      if (ticket !== loadSeq) return;
      if (!alive()) return settle();
      set({ error: e instanceof Error ? e.message : String(e), loading: false });
    }
  },

  loadMembers: async () => {
    const id = get().currentId;
    if (!id) return;
    const alive = sessionGuard();
    const members = await getDataSource().listMembers(id);
    // `select` clears members on a workspace switch; a late list for the
    // previous workspace must not refill it with the other tenant's people.
    if (!alive() || get().currentId !== id) return;
    set({ members });
  },

  loadAllWorkspaces: async () => {
    const alive = sessionGuard();
    const allWorkspaces = await getDataSource().listAllWorkspaces();
    if (!alive()) return;
    set({ allWorkspaces });
  },

  select: (id) => {
    localStorage.setItem(LAST_WS_KEY, id);
    // Switching tenants ends the session: anything still in flight was issued
    // for the workspace being left, and a pending createKey would otherwise
    // publish that workspace's one-time secret under this one. The store owns
    // this rather than the call site, which is free to forget. Clearing the
    // cached rows is still the caller's job (see WorkspaceMenu) — endSession
    // only invalidates, it does not wipe.
    endSession();
    set({ currentId: id, members: [] });
  },

  create: async (draft) => {
    const alive = sessionGuard();
    const ws = await getDataSource().createWorkspace(draft);
    // The workspace exists either way, but it belongs to the identity that
    // asked for it — never select it into someone else's session.
    if (!alive()) return ws;
    localStorage.setItem(LAST_WS_KEY, ws.id);
    // Creating a workspace switches into it, so it ends the session for the
    // same reason `select` does. The list itself belongs to the user, not to
    // the workspace being left, so it survives.
    endSession();
    set((s) => ({ workspaces: [ws, ...s.workspaces], currentId: ws.id, members: [] }));
    return ws;
  },

  invite: async (draft) => {
    const id = get().currentId;
    if (!id) throw new Error("no workspace selected");
    const alive = sessionGuard();
    const member = await getDataSource().inviteMember(id, draft);
    if (!alive() || get().currentId !== id) return member;
    set((s) => ({ members: [member, ...s.members] }));
    return member;
  },

  reset: () => {
    // Invalidate anything in flight so a late response can't repopulate.
    endSession();
    set({
      workspaces: [], currentId: null, members: [], allWorkspaces: [],
      loading: false, error: null,
    });
  },
}));
